import { ArrowLeft, Building2, CalendarDays, ChartColumn, ClipboardList, ClipboardPen, FileText, House, Inbox, LayoutTemplate, LogOut, Megaphone, NotebookPen, Package, Receipt, Search, Settings, Sparkles, Trash2, Wallet, X } from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { QuoteSubNav } from "./QuoteSubNav";
import { WorkOrderSubNav } from "./WorkOrderSubNav";
import { SectionNav } from "./SectionNav";
import type { SectionNavId, SectionNavItem } from "./SectionNav";
import { canSeeNav } from "./navRoles";
import { BrandMark } from "../components/BrandMark";
import { commandPaletteHint } from "../lib/keyboard";
import { api } from "../api/client";
import { useAuth } from "../features/auth/useAuth";
import { useUnreadUpdates } from "../features/updates/unread";
import { useMailboxUnread } from "../features/mailbox/useMailboxUnread";
import type { UserRole } from "../api/types";

// 側邊欄。上半段是主導覽,分隔線之後是報價分類導覽(只在報價單編輯頁出現),
// 最底下是目前登入者與登出。
// 圖示一律 lucide-react,不用 emoji。
//
// 主導覽**按角色過濾**(第十二輪,設計規格 §2.3):財務登入後看到的是
// 報價單與請假兩項,第 15 輪起再長出發票、雜支與報表。角色的對照表在
// `navRoles.ts`,**⌘K 指令面板用的是同一份**(那是這個系統的另一個全域導覽面,
// 兩邊各寫一份的話,過濾掉的入口從 ⌘K 那邊照樣點得進去)。

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  /** 這一項之前要不要起一個新的小標題。**只有每一段的第一項填它。** */
  group?: string;
}

// **角色寫在路徑上,渲染時過濾** —— 那張對照表在 `navRoles.ts`,
// 與 ⌘K 指令面板共用同一份(它是這個系統的另一個全域導覽面)。
//
// 不要改成在下面的 JSX 裡寫 `role === "FINANCE" ? ... : ...`:那種字串比對一旦
// 散出去,第 15 輪長出發票、雜支與報表(那三項是老闆與財務看得到、同仁看不到,
// 與這裡每一項的答案都不一樣)時一定會漏掉其中幾處,而漏掉的那幾處是
// 「某個角色看得到不該看的入口」。加一項就是在 `NAV_ROLES` 加一列。
const NAV_ITEMS: NavItem[] = [
  // 首頁排最前面 —— 它是每個人登入後的落點(業主 2026-09-17)。
  { to: "/home", label: "首頁", icon: <House aria-hidden="true" size={16} /> },
  { to: "/quotes", label: "報價單", icon: <FileText aria-hidden="true" size={16} />, group: "業務" },
  { to: "/templates", label: "範本", icon: <LayoutTemplate aria-hidden="true" size={16} /> },
  { to: "/catalog", label: "品項庫", icon: <Package aria-hidden="true" size={16} /> },
  { to: "/companies", label: "客戶", icon: <Building2 aria-hidden="true" size={16} /> },
  // 製單(業主 2026-10-08)。全員看得到 —— 在「業務」這一段,財務也看得到這一項。
  { to: "/workorders", label: "製單", icon: <ClipboardPen aria-hidden="true" size={16} /> },
  // 會議紀錄 / 結案報告 / 檢討報告(第 18 輪)。**全員看得到** ——
  // 一份只有作者看得到的會議紀錄沒有意義,它就是給沒去開會的人看的。
  { to: "/meetings", label: "會議紀錄", icon: <NotebookPen aria-hidden="true" size={16} />, group: "溝通" },
  // 信箱要在系統裡開,不另開一個瀏覽器視窗(業主原話)。
  { to: "/mailbox", label: "信箱", icon: <Inbox aria-hidden="true" size={16} /> },
  // 公佈欄(業主 2026-09-25)。**全員** —— 每個人都能貼、也能移除;門口的打卡機輪播它。
  { to: "/bulletin", label: "公佈欄", icon: <Megaphone aria-hidden="true" size={16} /> },
  // 發票是財務的本業(第 15 輪)。同仁看不到 —— 角色寫在 `navRoles.ts`。
  { to: "/invoices", label: "發票", icon: <Receipt aria-hidden="true" size={16} />, group: "財務" },
  // 雜支的**全公司清單**是財務的頁面(第 16 輪)。登錄本身人人都做得到,
  // 但那個入口在報價單上,不在這裡。
  { to: "/expenses", label: "雜支", icon: <Wallet aria-hidden="true" size={16} /> },
  // 月 / 季 / 年報與業務績效(第 17 輪)。**業務看不到** ——
  // 應收帳款、營收與別人的績效不是業務該在側邊欄上看到的東西。
  // 「對帳」2026-09-30 併進「報表」成為分頁(財務整理 2:財務選單 4 → 3)。
  { to: "/reports", label: "報表", icon: <ChartColumn aria-hidden="true" size={16} /> },
  // 業主指定「請假」放在「設定」上方,所以這兩項的先後不是排版偏好,是規格。
  // 出勤紀錄與請假是同一類東西,排在一起(業主 2026-09-21:
  // 「每個人左側都要顯示自己的上下班打卡紀錄 請假紀錄」)。
  { to: "/attendance", label: "出勤紀錄", icon: <ClipboardList aria-hidden="true" size={16} />, group: "其他" },
  { to: "/leave", label: "請假", icon: <CalendarDays aria-hidden="true" size={16} /> },
  { to: "/settings", label: "設定", icon: <Settings aria-hidden="true" size={16} /> },
  // 更新紀錄與留言板(業主 2026-09-28)。**全員**;旁邊的紅點是還沒看過的更新項目數。
  { to: "/updates", label: "更新紀錄", icon: <Sparkles aria-hidden="true" size={16} /> },
  // 回收桶排在最底下 —— 這不是每天要去的地方,只有刪錯的時候才會想到它。
  { to: "/trash", label: "回收桶", icon: <Trash2 aria-hidden="true" size={16} /> },
];

/** 這個角色看得到的那幾項,順序照 `NAV_ITEMS` 原本的順序。 */
function navFor(role: UserRole | null | undefined): NavItem[] {
  return NAV_ITEMS.filter((item) => canSeeNav(item.to, role));
}

export interface SidebarProps {
  /** 開啟全域搜尋(⌘K)。 */
  onOpenSearch: () => void;
  /** 抽屜模式下的關閉鈕;桌機版固定顯示,不需要。 */
  onClose?: () => void;
  /** 是否為 <1024px 的抽屜模式。 */
  drawer?: boolean;
  sections?: SectionNavItem[];
  onJumpSection?: (id: SectionNavId) => void;
  activeSectionId?: SectionNavId | null;
}

/**
 * 公司名稱,給左上角那一行用(業主 2026-09-17:「LOGO的左邊應該要是要放 公司名稱」)。
 *
 * **讀後端的設定,不寫死。** 業主在設定頁改過公司抬頭之後,側邊欄不該還掛著
 * 舊的那一個 —— 報價單上印的是新的、側邊欄寫的是舊的,那比沒有名字更糟。
 *
 * **不走 react-query。** 側邊欄長在 `AppShell` 上,而 `AppShell` 幾乎每一支
 * 元件測試都會掛 —— 在這裡要求一個 QueryClientProvider,等於逼每一支測試
 * 都去包一層。與 `AttachmentPanel`、`ProjectCost`、首頁那幾塊同一個慣例。
 *
 * 那一份答案記在模組層:寬螢幕的側邊欄與窄螢幕的抽屜是兩個實例,
 * 不記的話同一個名字會撈兩次。
 *
 * 讀不到就回空字串,畫面只畫 LOGO —— 一行「載入中」或一塊空白留在每一頁的
 * 左上角,比少一個名字難看得多。
 */
let cachedCompanyName: Promise<string> | null = null;

function fetchCompanyName(): Promise<string> {
  if (cachedCompanyName === null) {
    cachedCompanyName = api
      .get<{ company_name_zh?: string }>("/settings")
      .then((got) => (got?.company_name_zh ?? "").trim())
      .catch(() => "");
  }
  return cachedCompanyName;
}

/** 測試用:把那一份記住的答案丟掉。 */
export function forgetCompanyName(): void {
  cachedCompanyName = null;
}

function useCompanyName(): string {
  const [name, setName] = useState("");
  useEffect(() => {
    let alive = true;
    void fetchCompanyName().then((got) => {
      if (alive) setName(got);
    });
    return () => {
      alive = false;
    };
  }, []);
  return name;
}

export function Sidebar({
  onOpenSearch,
  onClose,
  drawer = false,
  sections = [],
  onJumpSection,
  activeSectionId = null,
}: SidebarProps) {
  const { user, logout } = useAuth();
  const unreadUpdates = useUnreadUpdates();
  // 收件匣未讀數(規格 §2.4):推播一來就重問,不輪詢。沒啟用信箱就是 0、不畫。
  const unreadMail = useMailboxUnread();

  const companyName = useCompanyName();
  // 「報價單」的二級選單只在報價單那幾頁展開(業主 2026-10-15)。
  const { pathname } = useLocation();
  const onQuotes = pathname === "/quotes" || pathname.startsWith("/quotes/");
  // 「製單」的全部 / 已結案 / 未結案也一樣(業主 2026-11-02)。
  const onWorkOrders = pathname === "/workorders" || pathname.startsWith("/workorders/");

  return (
    <div className="flex h-full min-h-0 w-full flex-col gap-3 border-r border-[var(--border)] bg-[var(--surface)] p-3">
      {/* 頂部是名片上的 LOGO,不是先前那行純文字標題。
          它是側邊欄唯一的品牌標示,所以替代文字不可以空著 ——
          BrandMark 自己帶著 alt,這裡不要再包一層 aria-hidden 把它藏掉。
          底下那條金黃細線是分隔,不是狀態:金黃只做裝飾,不承載任何資訊。 */}
      <div className="flex items-center justify-between gap-2 border-b border-brand-gold px-1 pb-2">
        <div className="flex min-w-0 items-center gap-2">
          {/* 公司名稱**在 LOGO 左邊**(業主指名的位置)。
              名字比 LOGO 更會長,所以 truncate 的是名字那一半;LOGO 不縮。 */}
          {companyName ? (
            <span className="min-w-0 truncate text-sm font-semibold text-[var(--text)]">
              {companyName}
            </span>
          ) : null}
          <BrandMark className="h-9 w-auto shrink-0" />
        </div>
        {drawer && onClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="關閉選單"
            className="rounded-[var(--radius)] p-1 text-[var(--text-3)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]"
          >
            <X aria-hidden="true" size={18} />
          </button>
        ) : null}
      </div>

      {/* 搜尋入口同時提示快捷鍵,不必先知道 ⌘K 才找得到。 */}
      <button
        type="button"
        onClick={onOpenSearch}
        className="flex items-center gap-2 rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface-2)] px-2.5 py-1.5 text-sm text-[var(--text-3)] hover:border-[var(--accent)] hover:text-[var(--text-2)]"
      >
        <Search aria-hidden="true" size={16} />
        <span className="flex-1 text-left">搜尋</span>
        <kbd className="rounded border border-[var(--border-strong)] bg-[var(--surface)] px-1.5 py-0.5 text-xs">
          {commandPaletteHint()}
        </kbd>
      </button>

      {/* ==================================================================
          開著一張報價單的時候,側邊欄整個讓位給章節
          ==================================================================
          業主 2026-09-21:「報價單一打開 側邊欄下面只能顯示一兩項報價單的
          分類」。**問題不在章節,在主導覽已經占掉整個畫面** —— 十五項
          × 34px ≈ 510px,加上 LOGO、搜尋與底部的個人選單約 720px,
          而筆電的視窗高度大約 640px。章節那一段分到的是負的。

          所以那一頁不畫那十五項:**編輯一張單的時候唯一會用的導覽就是章節**
          (一張單常有七八個)。要去別頁還有 ⌘K,一鍵直達,本來就不必經過
          側邊欄 —— 只留一條路回報價單列表。 */}
      {sections.length > 0 ? (
        <>
          <nav aria-label="回到列表">
            <NavLink
              to="/quotes"
              end
              onClick={drawer ? onClose : undefined}
              className="flex items-center gap-2 rounded-[var(--radius)] px-3 py-1.5 text-sm text-[var(--text-2)] no-underline hover:bg-[var(--surface-2)] hover:text-[var(--text)]"
            >
              <ArrowLeft aria-hidden="true" size={16} />
              報價單
            </NavLink>
          </nav>

          <hr className="my-1" />

          <SectionNav
            sections={sections}
            onJump={(id) => {
              onJumpSection?.(id);
              if (drawer) onClose?.();
            }}
            activeId={activeSectionId}
            className="min-h-0 flex-1"
          />
        </>
      ) : (
        <nav aria-label="主導覽">
          <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
            {navFor(user?.role).map((item) => (
              <Fragment key={item.to}>
                {/* **小標題,不是收合鈕。** 收合看起來省空間,但它把負擔從
                    「捲動」換成「記憶」—— 使用者得先想起某一項屬於哪一組才
                    點得到,而每個人心裡的分組都不一樣。
                    `aria-hidden`:它是視覺上的分段,不是可操作的東西;
                    報讀器順著連結唸下去本來就讀得出順序。 */}
                {item.group ? (
                  <li aria-hidden="true" className="px-3 pb-0.5 pt-2.5">
                    <span className="text-xs font-medium tracking-wide text-[var(--text-3)]">
                      {item.group}
                    </span>
                  </li>
                ) : null}
                <li>
                  <NavLink
                    to={item.to}
                    onClick={drawer ? onClose : undefined}
                    className={({ isActive }) =>
                      "flex items-center gap-2.5 rounded-[var(--radius)] px-3 py-1.5 text-sm no-underline " +
                      (isActive
                        ? "bg-[var(--accent-soft)] font-medium text-[var(--accent-hover)]"
                        : "text-[var(--text-2)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]")
                    }
                  >
                    {item.icon}
                    {item.label}
                    {item.to === "/updates" && unreadUpdates > 0 ? (
                      // 紅色實心小圓、裡面是數字。**數字本身就是資訊**,不只靠顏色;
                      // 顏色走代幣(--on-danger 在深色主題下是深字),不寫死白字。
                      <span
                        aria-label={`${unreadUpdates} 項新的更新`}
                        className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--danger)] px-1.5 text-xs font-semibold leading-none text-[var(--on-danger)]"
                      >
                        {unreadUpdates > 99 ? "99+" : unreadUpdates}
                      </span>
                    ) : null}
                    {item.to === "/mailbox" && unreadMail > 0 ? (
                      <span
                        aria-label={`${unreadMail} 封未讀信`}
                        className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--accent)] px-1.5 text-xs font-semibold leading-none text-[var(--on-accent)]"
                      >
                        {unreadMail > 99 ? "99+" : unreadMail}
                      </span>
                    ) : null}
                  </NavLink>
                  {item.to === "/quotes" && onQuotes ? <QuoteSubNav onPicked={drawer ? onClose : undefined} /> : null}
                  {item.to === "/workorders" && onWorkOrders ? <WorkOrderSubNav onPicked={drawer ? onClose : undefined} /> : null}
                </li>
              </Fragment>
            ))}
          </ul>
        </nav>
      )}

      {/* 個人選單:目前登入者、修改密碼與登出。
          沒有登入狀態時(例如單獨 render 外框)整塊不出現。
          兩個動作都直接看得到 —— 收進展開式選單只會讓「我的密碼在哪裡改」
          變成一個要先猜對才問得出來的問題。

          `mt-auto` 把上方剩下的空間全部吃掉,這一塊就一直貼在側邊欄最底部。
          少了它,個人選單只是緊跟在主導覽後面:開著報價單時中間又多一段七項的
          報價分類導覽,它被一路往下推,離「設定」很遠卻又不在底部 ——
          每開一張單位置就換一次,使用者得重新找一遍自己的帳號在哪裡
          (業主原話:「應該要一直維持在左側網頁最底部」)。 */}
      {user ? (
        <div
          role="group"
          aria-label="個人選單"
          className="mt-auto flex items-center gap-2 border-t border-[var(--border)] pt-2"
        >
          {/* 名字與 Email 在左、登出在同一列最右邊,垂直置中(業主 2026-09-25)。
              文字那一半可以縮、會截斷;登出不縮 —— 長 Email 不可以把它擠出去。
              修改密碼在 2026-09-25 拿掉了,入口只剩「設定 → 個人資料」。 */}
          <div className="min-w-0 flex-1 px-1">
            <p className="m-0 truncate text-sm font-medium text-[var(--text)]">{user.name}</p>
            <p className="m-0 truncate text-xs text-[var(--text-3)]">{user.email}</p>
          </div>
          <button
            type="button"
            onClick={() => {
              void logout();
            }}
            className="flex shrink-0 items-center gap-1.5 rounded-[var(--radius)] px-2 py-1.5 text-sm text-[var(--text-2)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]"
          >
            <LogOut aria-hidden="true" size={16} />
            登出
          </button>
        </div>
      ) : null}
    </div>
  );
}
